import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = [
    "form",
    "preview",
    "loadingState",
    "contentState",
    "quantityInput",
    "labourRateInput",
    "downtimeInput",
    "impactSlider",
    "impactValue",
    "yearsSelect",
    "calculateButton",
    "previewAsafe",
    "previewSteel",
    "previewBoplan",
  ]

  static values = {
    debounceMs: { type: Number, default: 500 },
    productProfileId: String,
    assessmentId: String,
    previewUrl: String,
    defaultQuantity: { type: Number, default: 10 },
    defaultLabourRate: { type: Number, default: 55.0 },
    defaultDowntimeCost: { type: Number, default: 0 },
    defaultImpactFrequency: { type: Number, default: 2 },
    defaultYears: { type: Number, default: 5 },
  }

  // CSS classes configurable via data attributes
  static classes = ["error"]

  connect() {
    this.debounceTimeout = null
  }

  disconnect() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
  }

  updatePreview() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    this.debounceTimeout = setTimeout(() => {
      this.fetchPreview()
    }, this.debounceMsValue)
  }

  async fetchPreview() {
    if (!this.previewUrlValue) return

    this.showLoading()

    const formData = this.collectFormData()
    const params = new URLSearchParams(formData)
    const url = `${this.previewUrlValue}?${params.toString()}`

    try {
      const response = await get(url, {
        responseKind: "json",
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")

        if (contentType && contentType.includes("turbo-stream")) {
          const html = await response.text
          window.Turbo.renderStreamMessage(html)
        } else {
          const data = await response.json
          this.updatePreviewDisplay(data)
        }
      }
    } catch {
      // Silently fail - preview is non-critical
    } finally {
      this.hideLoading()
    }
  }

  showLoading() {
    if (this.hasLoadingStateTarget)
      this.loadingStateTarget.classList.remove("hidden")
    if (this.hasContentStateTarget)
      this.contentStateTarget.classList.add("hidden")
  }

  hideLoading() {
    if (this.hasLoadingStateTarget)
      this.loadingStateTarget.classList.add("hidden")
    if (this.hasContentStateTarget)
      this.contentStateTarget.classList.remove("hidden")
  }

  collectFormData() {
    const data = {}

    if (this.hasQuantityInputTarget) {
      data.quantity = this.quantityInputTarget.value
    }
    if (this.hasLabourRateInputTarget) {
      data.labour_rate_gbp = this.labourRateInputTarget.value
    }
    if (this.hasDowntimeInputTarget) {
      data.downtime_hourly_cost_gbp = this.downtimeInputTarget.value
    }
    if (this.hasImpactSliderTarget) {
      data.impact_frequency = this.impactSliderTarget.value
    }
    if (this.hasYearsSelectTarget) {
      data.projection_years = this.yearsSelectTarget.value
    }
    if (this.productProfileIdValue) {
      data.product_profile_id = this.productProfileIdValue
    }

    return data
  }

  updatePreviewDisplay(data) {
    if (this.hasPreviewAsafeTarget && data.asafe_total) {
      this.previewAsafeTarget.textContent = this.formatCurrency(
        data.asafe_total,
      )
    }
    if (this.hasPreviewSteelTarget && data.steel_total) {
      this.previewSteelTarget.textContent = this.formatCurrency(
        data.steel_total,
      )
    }
    if (this.hasPreviewBoplanTarget && data.boplan_total) {
      this.previewBoplanTarget.textContent = this.formatCurrency(
        data.boplan_total,
      )
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  sliderChange() {
    if (this.hasImpactSliderTarget && this.hasImpactValueTarget) {
      this.impactValueTarget.textContent = this.impactSliderTarget.value
    }
    this.updatePreview()
  }

  calculate() {
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit()
    }
  }

  reset() {
    if (this.hasQuantityInputTarget) {
      this.quantityInputTarget.value = this.defaultQuantityValue
    }
    if (this.hasLabourRateInputTarget) {
      this.labourRateInputTarget.value = this.defaultLabourRateValue
    }
    if (this.hasDowntimeInputTarget) {
      this.downtimeInputTarget.value = this.defaultDowntimeCostValue
    }
    if (this.hasImpactSliderTarget) {
      this.impactSliderTarget.value = this.defaultImpactFrequencyValue
      if (this.hasImpactValueTarget) {
        this.impactValueTarget.textContent = String(
          this.defaultImpactFrequencyValue,
        )
      }
    }
    if (this.hasYearsSelectTarget) {
      this.yearsSelectTarget.value = this.defaultYearsValue
    }

    this.updatePreview()
  }

  validateInputs() {
    let valid = true

    if (this.hasQuantityInputTarget) {
      const quantity = parseInt(this.quantityInputTarget.value, 10)
      if (isNaN(quantity) || quantity <= 0) {
        this.errorClasses.forEach((cls) =>
          this.quantityInputTarget.classList.add(cls),
        )
        valid = false
      } else {
        this.errorClasses.forEach((cls) =>
          this.quantityInputTarget.classList.remove(cls),
        )
      }
    }

    return valid
  }
}
